// Weighbridge Kiosk Mode — session store.
//
// The kiosk lives OUTSIDE the main app's ProtectedRoute/authStore flow: it
// has its own login surface (PIN / password) and its own lightweight session
// state. Tokens still land in the shared tenant storage slots so every
// existing /wb/* hook + the apiClient refresh interceptor work unchanged.
//
// Two pieces of state:
//   - the logged-in operator (name / role) — in memory, re-hydrated from
//     /auth/me on boot when an access token is already present.
//   - the weighbridge this physical device is locked to — persisted in
//     localStorage (survives staff logout; only "Change weighbridge" clears).

import { create } from 'zustand';
import { apiClient, ensureFreshToken, onLogoutEvent } from '../api/client';
import { endpoints } from '../api/endpoints';
import { storage } from '../utils/storage';
import { kioskLogin } from '../api/kiosk';
import type { LoginResult, MeResult, StaffRole } from '../types/api';

export type KioskStatus = 'loading' | 'authenticated' | 'anonymous';

export interface KioskStaff {
  readonly name: string;
  readonly role: StaffRole;
  readonly username?: string;
}

interface KioskState {
  status: KioskStatus;
  staff: KioskStaff | null;
  weighbridgeEncId: string | null;
  _unsubLogout?: () => void;
  _refreshInterval?: ReturnType<typeof setInterval>;
}

interface KioskActions {
  bootstrap: () => Promise<void>;
  loginWithPin: (staffEncId: string, pin: string) => Promise<void>;
  loginWithPassword: (username: string, password: string) => Promise<void>;
  logout: () => void;
  lockWeighbridge: (encId: string) => void;
  clearWeighbridge: () => void;
}

export type KioskStore = KioskState & KioskActions;

function storeTokens(accessToken: string, refreshToken: string): void {
  storage.set('accessToken', accessToken);
  storage.set('refreshToken', refreshToken);
  storage.set('realm', 'staff');
}

export const useKioskStore = create<KioskStore>((set, get) => ({
  status: 'loading',
  staff: null,
  weighbridgeEncId: storage.get('kioskWeighbridge'),

  bootstrap: async () => {
    // Guard against double-subscribe and double-interval on hot reload / re-mount.
    get()._unsubLogout?.();
    const existingInterval = get()._refreshInterval;
    if (existingInterval !== undefined) clearInterval(existingInterval);

    const unsubLogout = onLogoutEvent('tenant', () => {
      set({ status: 'anonymous', staff: null });
    });

    // Proactive token refresh 2 minutes before the 15-minute TTL expires.
    // Prevents multiple concurrent tabs from racing to refresh simultaneously
    // when tokens expire reactively.
    const refreshInterval = setInterval(() => {
      void ensureFreshToken('tenant');
    }, 13 * 60 * 1000);

    set({ _unsubLogout: unsubLogout, _refreshInterval: refreshInterval });

    const weighbridgeEncId = storage.get('kioskWeighbridge');
    const token = storage.get('accessToken');
    if (!token) {
      set({ status: 'anonymous', staff: null, weighbridgeEncId });
      return;
    }
    try {
      const res = await apiClient.get<MeResult>(endpoints.auth.me);
      const user = res.data.user;
      set({
        status: 'authenticated',
        staff: { name: user.name, role: user.role as StaffRole },
        weighbridgeEncId,
      });
    } catch {
      // Token invalid / expired and refresh failed — back to the picker.
      storage.clearTenant();
      set({ status: 'anonymous', staff: null, weighbridgeEncId });
    }
  },

  loginWithPin: async (staffEncId, pin) => {
    const result = await kioskLogin(staffEncId, pin);
    storeTokens(result.accessToken, result.refreshToken);
    set({
      status: 'authenticated',
      staff: {
        name: result.staff.name,
        role: result.staff.role,
        username: result.staff.username,
      },
    });
  },

  loginWithPassword: async (username, password) => {
    const res = await apiClient.post<LoginResult>(endpoints.auth.login, {
      username,
      password,
    });
    const { access_token, refresh_token, user } = res.data;
    storeTokens(access_token, refresh_token);
    set({
      status: 'authenticated',
      staff: { name: user.name, role: user.role as StaffRole },
    });
  },

  logout: () => {
    get()._unsubLogout?.();
    const interval = get()._refreshInterval;
    if (interval !== undefined) clearInterval(interval);
    set({ _unsubLogout: undefined, _refreshInterval: undefined });
    storage.clearTenant();
    set({ status: 'anonymous', staff: null });
  },

  lockWeighbridge: (encId) => {
    storage.set('kioskWeighbridge', encId);
    set({ weighbridgeEncId: encId });
  },

  clearWeighbridge: () => {
    storage.remove('kioskWeighbridge');
    set({ weighbridgeEncId: null });
  },
}));
